import { type DatePickerProps } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CustomDatePicker from './CustomDatePicker';
import { useEffect, useState } from 'react';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props extends DatePickerProps<Dayjs> {
  format?: string;
  startDate: Dayjs | Date | null;
  endDate: Dayjs | Date | null;
  onFinalDateChange?: (range: [Date | null, Date | null]) => void;
  maxDaysFromStartDate?: number;
}

const CustomTimeZoneDateRangePicker = ({
  format = 'DD-MM-YYYY',
  startDate,
  endDate,
  onFinalDateChange,
  maxDate,
  maxDaysFromStartDate,
  ...props
}: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const [startDateValue, setStartDateValue] = useState<Dayjs | null>(
    startDate ? dayjs.utc(startDate).tz(timezoneBasedOnDisplayMode) : null
  );
  const [endDateValue, setEndDateValue] = useState<Dayjs | null>(
    endDate ? dayjs.utc(endDate).tz(timezoneBasedOnDisplayMode) : null
  );
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  useEffect(() => {
    if (startDate)
      setStartDateValue(dayjs.utc(startDate).tz(timezoneBasedOnDisplayMode));
    if (endDate)
      setEndDateValue(dayjs.utc(endDate).tz(timezoneBasedOnDisplayMode));
  }, [startDate, endDate, timezoneBasedOnDisplayMode]);

  const callFinalDateChange = (end: Dayjs | null) => {
    if (onFinalDateChange && startDateValue && end) {
      onFinalDateChange([
        startDateValue.tz(timezoneBasedOnDisplayMode).utc().toDate(),
        end.tz(timezoneBasedOnDisplayMode).utc().toDate(),
      ]);
    }
    setEndDatePickerOpen(false);
  };

  return timezoneBasedOnDisplayMode ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: 'fit-content',
        height: 'fit-content',
      }}
    >
      <CustomDatePicker
        {...props}
        format={format}
        value={startDateValue}
        timezone={timezoneBasedOnDisplayMode}
        onChange={(date) => {
          setStartDateValue(date);
          setEndDateValue(null);
          setEndDatePickerOpen(true);
        }}
        maxDate={maxDate}
      />
      <p
        style={{
          color: palette.textPrimary,
          margin: '0',
        }}
      >
        -
      </p>
      <CustomDatePicker
        {...props}
        open={endDatePickerOpen}
        onOpen={() => {
          setEndDatePickerOpen(true);
        }}
        onClose={() => {
          setEndDatePickerOpen(false);
        }}
        format={format}
        value={endDateValue}
        timezone={timezoneBasedOnDisplayMode}
        onChange={(date) => {
          setEndDateValue(date);
          if (date) callFinalDateChange(date);
        }}
        minDate={startDateValue ?? undefined}
        maxDate={
          maxDaysFromStartDate !== undefined
            ? dayjs(startDateValue).add(maxDaysFromStartDate, 'day')
            : maxDate
        }
      />
    </div>
  ) : null;
};

export default CustomTimeZoneDateRangePicker;
